import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Crop as CropIcon, Download } from 'lucide-react';
import ReactCrop, { 
  type Crop, 
  type PixelCrop,
  centerCrop,
  makeAspectCrop
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Button from './Button';

interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageFile: File;
  aspectRatio: number;
  onCropComplete: (croppedFile: File) => void;
  maxWidth?: number;
  maxHeight?: number;
}

export default function ImageCropModal({
  isOpen,
  onClose,
  imageFile,
  aspectRatio,
  onCropComplete,
  maxWidth = 1200,
  maxHeight = 400
}: ImageCropModalProps) {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Load image when file changes
  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setImageSrc(e.target.result as string);
        }
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  // Initialize crop when image loads
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspectRatio,
        width,
        height
      ),
      width,
      height
    );
    
    setCrop(crop);
  }, [aspectRatio]);

  // Create cropped canvas
  const createCroppedImage = useCallback(async (): Promise<File | null> => {
    const image = imgRef.current;
    const crop = completedCrop;

    if (!image || !crop) {
      return null;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Set canvas dimensions to crop size
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    // Draw the cropped image
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }

          // Generate filename with crop suffix
          const originalName = imageFile.name;
          const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
          const ext = originalName.substring(originalName.lastIndexOf('.')) || '.jpg';
          const croppedName = `${nameWithoutExt}_cropped${ext}`;

          const file = new File([blob], croppedName, {
            type: 'image/jpeg'
          });

          resolve(file);
        },
        'image/jpeg',
        0.9
      );
    });
  }, [completedCrop, imageFile.name]);

  // Handle crop completion
  const handleCropComplete = async () => {
    setIsProcessing(true);
    
    try {
      const croppedFile = await createCroppedImage();
      if (croppedFile) {
        onCropComplete(croppedFile);
        onClose();
      }
    } catch (error) {
      console.error('Error creating cropped image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle close with cleanup
  const handleClose = () => {
    setImageSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    setIsProcessing(false);
    onClose();
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="crop-modal-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={handleClose}
        aria-label="Close crop editor"
      />
      
      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-4xl h-full max-h-[95vh] md:max-h-[90vh] flex flex-col bg-surface border border-border rounded-lg mx-2 md:mx-4 oro-modal-enter">
        {/* Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-border">
          <h2 id="crop-modal-title" className="text-base md:text-lg font-semibold text-text-primary">
            <CropIcon className="inline w-4 h-4 md:w-5 md:h-5 mr-2" />
            Crop Image
          </h2>
          <button
            onClick={handleClose}
            className="w-12 h-12 md:w-10 md:h-10 bg-surface-alt hover:bg-border text-text-secondary hover:text-text-primary rounded-md flex items-center justify-center transition-colors"
            aria-label="Close crop editor"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Crop Instructions */}
        <div className="px-3 md:px-4 py-2 md:py-3 border-b border-border bg-surface-alt">
          <p className="text-xs md:text-sm text-text-secondary">
            <span className="hidden sm:inline">Drag the corners to adjust your crop area. The image will be resized to fit the 3:1 aspect ratio.</span>
            <span className="sm:hidden">Pinch and drag to crop. 3:1 ratio will be maintained.</span>
          </p>
        </div>

        {/* Image Crop Area */}
        <div className="flex-1 overflow-auto p-2 md:p-4">
          <div className="w-full h-full flex items-center justify-center touch-manipulation">
            {imageSrc && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspectRatio}
                minWidth={80} // Smaller on mobile for easier manipulation
                minHeight={80 / aspectRatio}
                keepSelection
                ruleOfThirds
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop preview"
                  onLoad={onImageLoad}
                  className="max-w-full max-h-full"
                />
              </ReactCrop>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 md:p-4 border-t border-border bg-surface-alt">
          <div className="text-xs md:text-sm text-text-muted text-center sm:text-left">
            <span className="hidden sm:inline">Aspect ratio: {aspectRatio}:1 • Max size: {maxWidth}×{maxHeight}px</span>
            <span className="sm:hidden">{aspectRatio}:1 ratio • {maxWidth}×{maxHeight}px max</span>
          </div>
          
          <div className="flex gap-3 justify-center sm:justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={isProcessing}
              className="flex-1 sm:flex-initial"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCropComplete}
              disabled={!completedCrop || isProcessing}
              loading={isProcessing}
              className="flex-1 sm:flex-initial"
            >
              <Download className="w-4 h-4 mr-2" />
              Apply Crop
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}